// Display constants + tiny helpers for the v2 comparison pages. All business logic
// (grouping, verdict segments, reasons, chips, full names, service locations, the
// summary matrix, and review causes) is computed on the backend and returned by the
// v2 API — these pages only render it. Nothing here re-derives comparison semantics.

export const TONES = {
  slate: { fg: "#475467", bg: "#F2F4F7", ring: "#E4E7EC", dot: "#98A2B3" },
  sky: { fg: "#026AA2", bg: "#E0F2FE", ring: "#B9E6FE", dot: "#0BA5EC" },
  emerald: { fg: "#067647", bg: "#ECFDF3", ring: "#ABEFC6", dot: "#17B26A" },
  rose: { fg: "#B42318", bg: "#FEF3F2", ring: "#FECDCA", dot: "#F04438" },
  amber: { fg: "#B54708", bg: "#FFFAEB", ring: "#FEDF89", dot: "#F79009" },
  violet: { fg: "#6941C6", bg: "#F4F3FF", ring: "#D9D6FE", dot: "#7A5AF8" },
};

export const DARK = "#1D2733";
export const MUTED = "#98A2B3";

export const CHANGE_TABS = [
  { id: "adds", label: "Adds" },
  { id: "terms", label: "Terms" },
  { id: "changes", label: "Changes" },
];

// Roster segments (non-verdict). Providers are filtered by their backend `segment`
// / `inCustomer` / `inPrime` fields — no client-side classification.
export const SEGMENTS = [
  { id: "customer_only", label: "Only in customer roster", tone: "violet", test: (p) => p.segment === "customer_only" },
  { id: "prime_only", label: "Only in PRIME roster", tone: "rose", test: (p) => p.segment === "prime_only" },
  { id: "both", label: "Present in both rosters", tone: "emerald", test: (p) => p.segment === "both" },
];

export const REFERENCE = [
  { id: "customer_data", label: "Customer roster data", tone: "slate", test: (p) => p.inCustomer },
  { id: "prime_data", label: "PRIME roster data", tone: "slate", test: (p) => p.inPrime },
];

// Verdict is decided per side:
//  • Customer — matched and present in both rosters (segment "both") is valid; a record with no
//    match in the PRIME roster (segment "customer_only") is invalid.
//  • PRIME — a record that carries a reason is valid; a record with no reason available is invalid.
//    "Reason" is the value shown in the grid's Reason column, which is resolved from aiReason / the
//    change-history analysis / the roster's own Reason — so the prime split is applied in
//    ReconciliationV2 (which has that resolver). The tests here are the fallback membership predicate.
export const VERDICT_SEGMENTS = [
  { id: "valid_customer", label: "Valid in customer roster", tone: "emerald", test: (p) => p.segment === "both" },
  { id: "invalid_customer", label: "Invalid in customer roster", tone: "rose", test: (p) => p.segment === "customer_only" },
  { id: "valid_prime", label: "Valid in PRIME roster", tone: "emerald", test: (p) => p.inPrime },
  { id: "invalid_prime", label: "Invalid in PRIME roster", tone: "rose", test: (p) => p.inPrime },
];

// Backend-driven segment counts for the active group (from model.groups[group]).
export function groupCount(groupStats, segId) {
  if (!groupStats) return 0;
  const map = {
    customer_only: groupStats.customerOnly, prime_only: groupStats.primeOnly, both: groupStats.both,
    customer_data: groupStats.customerData, prime_data: groupStats.primeData,
  };
  return map[segId] ?? 0;
}

// De-duplicate rows by NPI or NPI + service location (uses backend fields only).
export function dedupeV2(rows, mode) {
  if (mode === "none") return rows;
  const seen = new Set();
  const out = [];
  for (const p of rows) {
    const key = mode === "npi" ? (p.npi || p.id) : `${p.npi}#${p.serviceLocation}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(p);
  }
  return out;
}

// Compare two cell values: numeric when both are numbers, otherwise case-insensitive text.
export function compareValues(a, b) {
  const na = Number(a), nb = Number(b);
  if (String(a).trim() !== "" && String(b).trim() !== "" && !Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
}

// Sort a list by a column: valueOf(row, col) yields the comparable value. Empty values always
// sink to the bottom (both directions), and ties keep their original order (stable).
export function sortRows(rows, col, dir, valueOf) {
  if (!col) return rows;
  const mult = dir === "asc" ? 1 : -1;
  return rows
    .map((r, i) => ({ r, i, v: valueOf(r, col) }))
    .sort((x, y) => {
      const xe = x.v === "" || x.v == null, ye = y.v === "" || y.v == null;
      if (xe && ye) return x.i - y.i;
      if (xe) return 1;
      if (ye) return -1;
      const c = compareValues(x.v, y.v);
      return c !== 0 ? c * mult : x.i - y.i;
    })
    .map((w) => w.r);
}

export const csvEscape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
export function downloadCsv(filename, rows) {
  const csv = rows.map((r) => r.map(csvEscape).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
